import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { API_CONFIG, buildApiUrl } from '../config/api';

const LowStockParts = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [status, setStatus] = useState('all'); // all | out | low

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const url = buildApiUrl(API_CONFIG.ENDPOINTS.REPORTS.LOW_STOCK_PARTS, {
          ...(status !== 'all' && { status })
        });
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Neuspješno dohvaćanje izvještaja.');
        const data = await res.json();
        setItems(data.items || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [status]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return items;
    return items.filter((d) => (
      String(d.naziv_dijela || '').toLowerCase().includes(q) ||
      String(d.kataloski_broj || '').toLowerCase().includes(q)
    ));
  }, [items, searchQuery]);

  const handleDownload = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const url = buildApiUrl(API_CONFIG.ENDPOINTS.REPORTS.LOW_STOCK_PARTS, {
        format,
        ...(status !== 'all' && { status }),
        ...(searchQuery.trim() && { q: searchQuery.trim() })
      });
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Neuspješno preuzimanje.');
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const suffix = status === 'all' ? '' : `-${status}`;
      a.download = format === 'pdf' ? `nedostatak-dijelova${suffix}.pdf` : `nedostatak-dijelova${suffix}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      alert(e.message);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[50vh] text-gray-600">Učitavanje...</div>;
  if (error) return (
    <div className="max-w-3xl mx-auto p-4">
      <div className="p-3 mb-4 text-sm text-red-700 bg-red-100 rounded-lg">Greška: {error}</div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-2xl font-semibold text-gray-900">Nedostatak dijelova</h2>
        <div className="w-full sm:w-80">
          <input
            type="text"
            placeholder="Pretraga (naziv, kataloški broj)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-2 py-2 bg-white border border-gray-300 rounded-md text-sm">
            <option value="all">Svi (niska i nema)</option>
            <option value="out">Nema na stanju</option>
            <option value="low">Niska zaliha</option>
          </select>
        </div>
        <div className="inline-flex items-center gap-2">
          <button onClick={() => handleDownload('pdf')} className="px-3 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 transition-colors inline-flex items-center gap-2">
            <FontAwesomeIcon icon={faFilePdf} />
            PDF
          </button>
          <button onClick={() => handleDownload('xlsx')} className="px-3 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors inline-flex items-center gap-2">
            <FontAwesomeIcon icon={faFileExcel} />
            Excel
          </button>
        </div>
      </div>

      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-red-50 low-stock-thead">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Dio</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Kataloški broj</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Dostupno</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Minimalno</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Upozorenje</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-gray-500">Sve zalihe su iznad minimalne količine.</td>
                </tr>
              ) : (
                filtered.map((d) => {
                  const out = Number(d.dostupna_kolicina) === 0;
                  return (
                    <tr key={d.id_dijelovi} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{d.naziv_dijela}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{d.kataloski_broj}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{d.dostupna_kolicina}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{d.minimalna_kolicina}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${out ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                          {out ? 'Nema na stanju' : 'Niska zaliha'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LowStockParts;
